
 // =========================================================
 // LA ESQUINA DEL PARQUÉS
 // STORES + MESAS
 // HERO EDITORIAL · CATÁLOGO PREMIUM
 // DJANGO API · MESAS DE DEMOSTRACIÓN
 // =========================================================

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  ArrowRight,
  ArrowUpRight,
  Armchair,
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  Crown,
  LoaderCircle,
  Mail,
  MapPin,
  Navigation,
  Phone,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Store as StoreIcon,
  UtensilsCrossed,
  Users,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";

import { Link } from "react-router-dom";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";
import API from "../../services/api";

import storeImage from "../../assets/bg.png";

import "../../styles/catalog.css";
import "../../styles/stores.css";

// =========================================================
// CONFIGURACIÓN
// =========================================================

const API_STORES = "/stores/";
const API_TABLES = "/store-tables/";

const STORE_NAME = "La Esquina del Parqués";

const STORE_FALLBACK = {
  name: STORE_NAME,
  address: "Dirección por configurar",
  city: "Medellín, Antioquia",
  phone: "",
  email: "",
  description:
    "Un espacio para disfrutar de buena comida, bebidas y partidas de parqués.",
  map_url: "",
};

const DEMO_TABLES = [
  {
    id: "demo-mesa-01",
    number: "01",
    name: "Mesa 01",
    capacity: 4,
    status: "available",
    description: "Ideal para compartir una buena partida.",
  },
  {
    id: "demo-mesa-02",
    number: "02",
    name: "Mesa 02",
    capacity: 4,
    status: "occupied",
    description: "Mesa con ocupación de demostración.",
  },
  {
    id: "demo-mesa-03",
    number: "03",
    name: "Mesa 03",
    capacity: 2,
    status: "available",
    description: "Un espacio cómodo para dos personas.",
  },
  {
    id: "demo-mesa-04",
    number: "04",
    name: "Mesa 04",
    capacity: 6,
    status: "reserved",
    description: "Reserva de ejemplo para mostrar el diseño.",
  },
  {
    id: "demo-mesa-05",
    number: "05",
    name: "Mesa 05",
    capacity: 4,
    status: "available",
    description: "Perfecta para reunirse y disfrutar.",
  },
  {
    id: "demo-mesa-06",
    number: "06",
    name: "Mesa 06",
    capacity: 2,
    status: "occupied",
    description: "Mesa con ocupación de demostración.",
  },
  {
    id: "demo-mesa-07",
    number: "07",
    name: "Mesa 07",
    capacity: 4,
    status: "available",
    description: "Un lugar para disfrutar en buena compañía.",
  },
  {
    id: "demo-mesa-08",
    number: "08",
    name: "Mesa 08",
    capacity: 6,
    status: "maintenance",
    description: "Estado de mantenimiento de ejemplo.",
  },
];

const TABLE_STATES = {
  available: {
    label: "Disponible",
    color: "#00d9a0",
    description: "Esta mesa aparece disponible.",
  },
  occupied: {
    label: "Ocupada",
    color: "#ff647c",
    description: "Esta mesa aparece ocupada.",
  },
  reserved: {
    label: "Reservada",
    color: "#ffc857",
    description: "Esta mesa aparece reservada.",
  },
  maintenance: {
    label: "Mantenimiento",
    color: "#a5afc4",
    description: "Esta mesa no está disponible para uso.",
  },
  unknown: {
    label: "Sin confirmar",
    color: "#a5afc4",
    description: "No se ha podido confirmar su estado.",
  },
};

// =========================================================
// HELPERS
// =========================================================

function normalizeText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getList(response, keys = []) {
  const data = response?.data ?? response;

  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

function getStoreName(store) {
  return (
    store?.name ||
    store?.nombre ||
    store?.title ||
    store?.store_name ||
    STORE_NAME
  );
}

function getId(item) {
  return item?.id ?? item?.pk ?? item?.uuid ?? null;
}

function getTableNumber(table, index = 0) {
  return (
    table?.number ??
    table?.numero ??
    table?.table_number ??
    table?.name ??
    table?.nombre ??
    `Mesa ${String(index + 1).padStart(2, "0")}`
  );
}

function getCapacity(table) {
  const capacity = Number(
    table?.capacity ??
      table?.capacidad ??
      table?.seats ??
      table?.personas ??
      0
  );

  return Number.isFinite(capacity) && capacity > 0
    ? capacity
    : null;
}

function getTableStatus(table) {
  const status = normalizeText(
    table?.status || table?.estado || table?.state || "unknown"
  );

  if (
    ["occupied", "ocupada", "ocupado", "in_use"].includes(status)
  ) {
    return "occupied";
  }

  if (
    ["reserved", "reservada", "reservado", "reservation"].includes(status)
  ) {
    return "reserved";
  }

  if (
    [
      "maintenance",
      "mantenimiento",
      "out_of_service",
      "fuera_de_servicio",
    ].includes(status)
  ) {
    return "maintenance";
  }

  if (
    ["available", "disponible", "libre", "free"].includes(status)
  ) {
    return "available";
  }

  return "unknown";
}

// =========================================================
// COMPONENTE
// =========================================================

export default function Stores() {
  const [store, setStore] = useState(null);
  const [tables, setTables] = useState(DEMO_TABLES);
  const [selectedTable, setSelectedTable] = useState(null);

  const [loadingStore, setLoadingStore] = useState(true);
  const [loadingTables, setLoadingTables] = useState(false);

  const [storeDemo, setStoreDemo] = useState(true);
  const [tablesDemo, setTablesDemo] = useState(true);

  const [storeError, setStoreError] = useState("");
  const [tablesError, setTablesError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // -------------------------------------------------------
  // CARGAR TIENDA
  // -------------------------------------------------------

  const loadStore = useCallback(async () => {
    setLoadingStore(true);
    setStoreError("");

    try {
      const response = await API.get(API_STORES);

      const stores = getList(response, [
        "results",
        "data",
        "stores",
        "items",
      ]);

      const foundStore = stores.find((item) => {
        const name = normalizeText(getStoreName(item));

        return (
          name === normalizeText(STORE_NAME) ||
          name.includes("la esquina del parques")
        );
      });

      if (!foundStore) {
        setStore(STORE_FALLBACK);
        setStoreDemo(true);
        setStoreError(
          "No se encontró la tienda en Django. Se muestra información de ejemplo."
        );
        return;
      }

      setStore(foundStore);
      setStoreDemo(false);
    } catch (error) {
      console.error("Error cargando la tienda:", error);

      setStore(STORE_FALLBACK);
      setStoreDemo(true);
      setStoreError(
        "No fue posible conectar con Django. Puedes seguir explorando la vista de demostración."
      );
    } finally {
      setLoadingStore(false);
    }
  }, []);

  // -------------------------------------------------------
  // CARGAR MESAS
  // -------------------------------------------------------

  const loadTables = useCallback(async () => {
    setTablesError("");

    const storeId = getId(store);

    if (!storeId || storeDemo) {
      setTables(DEMO_TABLES);
      setTablesDemo(true);
      setLoadingTables(false);
      return;
    }

    setLoadingTables(true);

    try {
      const response = await API.get(API_TABLES, {
        params: { store: storeId },
      });

      const list = getList(response, [
        "results",
        "data",
        "tables",
        "mesas",
        "items",
      ]);

      if (list.length > 0) {
        setTables(list);
        setTablesDemo(false);

        setSelectedTable((current) => {
          if (!current) return null;

          const currentId = getId(current);

          return (
            list.find(
              (table) => String(getId(table)) === String(currentId)
            ) || null
          );
        });
      } else {
        setTables(DEMO_TABLES);
        setTablesDemo(true);
        setTablesError(
          "La API no devolvió mesas registradas. Se muestran mesas de ejemplo."
        );
      }
    } catch (error) {
      console.error("Error cargando las mesas:", error);

      setTables(DEMO_TABLES);
      setTablesDemo(true);
      setTablesError(
        "No fue posible consultar las mesas en Django. Se muestran mesas de ejemplo."
      );
    } finally {
      setLoadingTables(false);
    }
  }, [store, storeDemo]);

  useEffect(() => {
    loadStore();
  }, [loadStore]);

  useEffect(() => {
    if (!loadingStore && store) {
      loadTables();
    }
  }, [loadingStore, store, loadTables]);

  // -------------------------------------------------------
  // FILTROS Y RESUMEN
  // -------------------------------------------------------

  const filteredTables = useMemo(() => {
    const query = normalizeText(search);

    return tables.filter((table, index) => {
      const number = normalizeText(getTableNumber(table, index));
      const name = normalizeText(table?.name || table?.nombre);
      const status = getTableStatus(table);

      const matchesSearch =
        !query || number.includes(query) || name.includes(query);

      const matchesStatus =
        statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [tables, search, statusFilter]);

  const summary = useMemo(() => {
    const result = {
      available: 0,
      occupied: 0,
      reserved: 0,
      maintenance: 0,
    };

    tables.forEach((table) => {
      const status = getTableStatus(table);

      if (Object.prototype.hasOwnProperty.call(result, status)) {
        result[status] += 1;
      }
    });

    return result;
  }, [tables]);

  const selectedTableIndex = tables.findIndex(
    (table) => String(getId(table)) === String(getId(selectedTable))
  );

  const currentTable =
    selectedTableIndex >= 0 ? tables[selectedTableIndex] : selectedTable;

  const selectedTableNumber = currentTable
    ? getTableNumber(currentTable, Math.max(0, selectedTableIndex))
    : "";

  const storeAddress =
    store?.address ||
    store?.direccion ||
    store?.location ||
    STORE_FALLBACK.address;

  const storePhone = store?.phone || store?.telefono || "";
  const storeEmail = store?.email || store?.correo || "";

  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------

  return (
    <>
      <Navbar />

      <main
        className="catalog-page stores-catalog-page"
        style={{ "--catalog-bg": `url(${storeImage})` }}
      >
        {/* HERO: MISMA COMPOSICIÓN EDITORIAL DEL CATÁLOGO */}

        <section
          className="catalog-hero blog-style-hero stores-hero"
          style={{ backgroundImage: `url(${storeImage})` }}
        >
          <div className="catalog-hero-overlay" />

          <div className="catalog-hero-inner">
            <div className="catalog-breadcrumb">
              <Link to="/">INICIO</Link>
              <ChevronRight size={13} />
              <span>LA TIENDA</span>
              <ChevronRight size={13} />
              <strong>MESAS</strong>
            </div>

            <div className="catalog-hero-copy">
              <span className="catalog-hero-kicker">
                LA ESQUINA DEL PARQUÉS · BAR & GASTROBAR
              </span>

              <h1>
                TU LUGAR.
                <em>TU MESA. TU MOMENTO.</em>
              </h1>

              <p>
                Descubre nuestros espacios, consulta los estados de las
                mesas y encuentra el lugar ideal para compartir comida,
                bebidas y buenas partidas de parqués.
              </p>

              <div className="stores-hero-actions">
                <a href="#mesas" className="catalog-hero-cta">
                  EXPLORAR MESAS
                  <ArrowRight size={16} />
                </a>

                <a href="#informacion-tienda" className="stores-hero-secondary">
                  CONOCER LA TIENDA
                  <ArrowUpRight size={15} />
                </a>
              </div>
            </div>

            <div className="catalog-hero-index">
              <span>BUENOS MOMENTOS</span>
              <strong>LA ESQUINA</strong>
            </div>
          </div>
        </section>

        {/* CONTENIDO */}

        <section className="stores-content" id="informacion-tienda">
          <header className="stores-section-heading">
            <div>
              <span className="stores-eyebrow">
                <Crown size={15} />
                NUESTRA CASA
              </span>

              <h2>La Esquina del Parqués</h2>

              <p>
                Un espacio para compartir, disfrutar y crear buenos momentos.
              </p>
            </div>

            <button
              type="button"
              className="stores-refresh-button"
              onClick={loadStore}
              disabled={loadingStore}
            >
              {loadingStore ? (
                <LoaderCircle className="stores-spin" size={16} />
              ) : (
                <RefreshCw size={16} />
              )}
              Actualizar tienda
            </button>
          </header>

          {storeDemo ? (
            <div className="stores-notice">
              <WifiOff size={18} />
              <div>
                <strong>Modo de demostración</strong>
                <p>
                  {storeError ||
                    "Se está mostrando información de ejemplo mientras se conecta la tienda real."}
                </p>
              </div>
            </div>
          ) : (
            <div className="stores-notice stores-notice-success">
              <Wifi size={18} />
              <div>
                <strong>Conectado con Django</strong>
                <p>La información de la tienda se obtuvo desde la API.</p>
              </div>
            </div>
          )}

          {store && (
            <article className="stores-info-card">
              <div className="stores-info-main">
                <span className="stores-eyebrow">
                  <StoreIcon size={15} />
                  {storeDemo ? "INFORMACIÓN DE EJEMPLO" : "TIENDA REGISTRADA"}
                </span>

                <h3>{getStoreName(store)}</h3>

                <p>
                  {store.description ||
                    store.descripcion ||
                    STORE_FALLBACK.description}
                </p>

                <div className="stores-info-line">
                  <MapPin size={18} />
                  <span>
                    <strong>Ubicación</strong>
                    <br />
                    {storeAddress}
                    <br />
                    {store.city || store.ciudad || STORE_FALLBACK.city}
                  </span>
                </div>

                {(store.opening_hours || store.horario) && (
                  <div className="stores-info-line">
                    <Clock3 size={18} />
                    <span>
                      <strong>Horario</strong>
                      <br />
                      {store.opening_hours || store.horario}
                    </span>
                  </div>
                )}
              </div>

              <div className="stores-info-contact">
                <div className="stores-info-line">
                  <Phone size={18} />
                  <span>
                    <strong>Teléfono</strong>
                    <br />
                    {storePhone || "Pendiente de configurar"}
                  </span>
                </div>

                <div className="stores-info-line">
                  <Mail size={18} />
                  <span>
                    <strong>Correo electrónico</strong>
                    <br />
                    {storeEmail || "Pendiente de configurar"}
                  </span>
                </div>

                {store.map_url && (
                  <a
                    className="stores-refresh-button"
                    href={store.map_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Navigation size={16} />
                    Cómo llegar
                    <ArrowUpRight size={15} />
                  </a>
                )}
              </div>
            </article>
          )}

          {storeError && !storeDemo && (
            <div className="stores-notice">
              <AlertCircle size={18} />
              <p>{storeError}</p>
            </div>
          )}
        </section>

        {/* MESAS */}

        <section className="stores-content stores-tables-section" id="mesas">
          <header className="stores-section-heading">
            <div>
              <span className="stores-eyebrow">
                <UtensilsCrossed size={15} />
                ESPACIOS Y EXPERIENCIAS
              </span>

              <h2>Nuestras mesas</h2>

              <p>
                Explora las mesas y selecciona una tarjeta para consultar
                su capacidad y estado.
              </p>
            </div>

            <button
              type="button"
              className="stores-refresh-button"
              onClick={loadTables}
              disabled={loadingTables}
            >
              {loadingTables ? (
                <LoaderCircle className="stores-spin" size={16} />
              ) : (
                <RefreshCw size={16} />
              )}
              Actualizar mesas
            </button>
          </header>

          {tablesDemo && (
            <div className="stores-notice">
              <Sparkles size={18} />
              <div>
                <strong>Mesas de demostración</strong>
                <p>
                  Los estados mostrados son ficticios y sirven para
                  previsualizar el diseño. No representan la ocupación real.
                </p>
              </div>
            </div>
          )}

          {tablesError && (
            <div className="stores-notice">
              <AlertCircle size={18} />
              <p>{tablesError}</p>
            </div>
          )}

          {/* RESUMEN */}

          <div className="stores-summary-grid">
            <div className="stores-summary-card available">
              <span>Disponibles <CheckCircle2 size={17} /></span>
              <strong>{summary.available}</strong>
            </div>

            <div className="stores-summary-card occupied">
              <span>Ocupadas <Users size={17} /></span>
              <strong>{summary.occupied}</strong>
            </div>

            <div className="stores-summary-card reserved">
              <span>Reservadas <Clock3 size={17} /></span>
              <strong>{summary.reserved}</strong>
            </div>

            <div className="stores-summary-card maintenance">
              <span>Mantenimiento <ShieldCheck size={17} /></span>
              <strong>{summary.maintenance}</strong>
            </div>
          </div>

          {/* BUSCADOR Y FILTROS */}

          <div className="stores-toolbar">
            <label className="stores-search">
              <Search size={18} />

              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por número o nombre..."
                aria-label="Buscar mesas"
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Limpiar búsqueda"
                >
                  <X size={16} />
                </button>
              )}
            </label>

            <select
              className="stores-status-select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              aria-label="Filtrar mesas por estado"
            >
              <option value="all">Todos los estados</option>
              <option value="available">Disponibles</option>
              <option value="occupied">Ocupadas</option>
              <option value="reserved">Reservadas</option>
              <option value="maintenance">Mantenimiento</option>
              <option value="unknown">Sin confirmar</option>
            </select>
          </div>

          {loadingTables && (
            <div className="stores-loading">
              <LoaderCircle className="stores-spin" size={20} />
              Consultando mesas...
            </div>
          )}

          {!loadingTables && filteredTables.length === 0 && (
            <div className="stores-empty">
              <Armchair size={32} />
              <h3>No encontramos mesas</h3>
              <p>Prueba otra búsqueda o cambia el filtro de estado.</p>
            </div>
          )}

          {!loadingTables && filteredTables.length > 0 && (
            <div className="stores-table-grid">
              {filteredTables.map((table, index) => {
                const id = getId(table);
                const status = getTableStatus(table);
                const config = TABLE_STATES[status];
                const capacity = getCapacity(table);

                const isSelected =
                  selectedTable &&
                  String(getId(selectedTable)) === String(id);

                return (
                  <button
                    type="button"
                    key={id ?? `table-${index}`}
                    className={`stores-table-card ${
                      isSelected ? "is-selected" : ""
                    }`}
                    onClick={() => setSelectedTable(table)}
                    aria-pressed={Boolean(isSelected)}
                  >
                    <div className="stores-table-card-top">
                      <span className="stores-table-icon">
                        <Armchair size={23} />
                      </span>

                      <span
                        className="stores-table-status"
                        style={{
                          color: config.color,
                          borderColor: `${config.color}66`,
                        }}
                      >
                        <CircleDot size={11} />
                        {config.label}
                      </span>
                    </div>

                    <h3>{getTableNumber(table, index)}</h3>

                    <span className="stores-table-capacity">
                      <Users size={15} />
                      {capacity
                        ? `${capacity} personas`
                        : "Capacidad no registrada"}
                    </span>

                    <span className="stores-table-footer">
                      VER DETALLES
                      <ChevronRight size={18} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* DETALLE DE MESA */}

          {currentTable && (
            <article className="stores-selected-detail">
              <div className="stores-detail-grid">
                <div>
                  <span className="stores-eyebrow">
                    <Armchair size={15} />
                    DETALLE DE MESA
                  </span>

                  <h3>{selectedTableNumber}</h3>

                  <p>
                    {currentTable.description ||
                      currentTable.descripcion ||
                      "Información de esta mesa."}
                  </p>

                  <div className="stores-info-line">
                    <CircleDot
                      size={18}
                      style={{
                        color: TABLE_STATES[
                          getTableStatus(currentTable)
                        ].color,
                      }}
                    />
                    <span>
                      <strong>
                        {TABLE_STATES[getTableStatus(currentTable)].label}
                      </strong>
                      <br />
                      {TABLE_STATES[getTableStatus(currentTable)].description}
                    </span>
                  </div>

                  <div className="stores-info-line">
                    <Users size={18} />
                    <span>
                      <strong>Capacidad</strong>
                      <br />
                      {getCapacity(currentTable)
                        ? `${getCapacity(currentTable)} personas`
                        : "No registrada"}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="stores-eyebrow">
                    <ShieldCheck size={15} />
                    INFORMACIÓN DEL SISTEMA
                  </span>

                  <div className="stores-info-line">
                    <StoreIcon size={18} />
                    <span>
                      <strong>Tienda</strong>
                      <br />
                      {getStoreName(store)}
                    </span>
                  </div>

                  <div className="stores-info-line">
                    <CircleDot size={18} />
                    <span>
                      <strong>Identificador</strong>
                      <br />
                      {getId(currentTable) ?? "Solo demostración"}
                    </span>
                  </div>

                  {tablesDemo && (
                    <div className="stores-notice">
                      <Sparkles size={17} />
                      <p>
                        Esta mesa es ficticia. Seleccionarla no crea una
                        reserva ni modifica datos en Django.
                      </p>
                    </div>
                  )}

                  <div className="stores-detail-actions">
                    <button
                      type="button"
                      className="stores-refresh-button"
                      onClick={() => setSelectedTable(null)}
                    >
                      Cerrar detalles
                    </button>

                    <a href="#mesas" className="stores-refresh-button">
                      Ver todas
                      <ArrowUpRight size={15} />
                    </a>
                  </div>
                </div>
              </div>
            </article>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
